'use client';
import { FC } from 'react';
import { Box, Button, Typography, Chip, LinearProgress, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import {
  Edit,
  CameraAlt,
  Save as SaveIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Publish as PublishIcon,
  Delete as DeleteIcon,
  RestartAlt as RestartAltIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';
import { PrivilegeEnum, StatusDto } from '@/api/ApiClient';
import { useDialogStore } from '@/stores/dialogStore';
import { STATUS_MAP } from '@/lib/types/statusTypes';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { log } from '@/lib/utils/logger';

interface DataPackageFooterProps {
  lastChangedDate?: Date;
  lastChangedBy: string;
  status: string;
  isReadOnly: boolean;
  hasChanges: boolean;
  onSave: () => void;
  onDelete: () => void;
  onCancel: () => void;
  setIsReadOnly: (val: boolean) => void;
  privilege: PrivilegeEnum;
  nodeId: string;
  // Snapshot creation props
  canCreateSnapshot?: boolean;
  /** When true, the primary button resumes a stuck snapshot build (POST restart-snapshot) instead of first-time PATCH. */
  isResumeSnapshot?: boolean;
  onCreateSnapshot?: () => void;
  // Snapshot progress
  isSnapshot?: boolean;
  snapshotInProgress?: boolean;
  snapshotProgress?: number;
  // Action handlers
  onSubmit?: () => void;
  onPublish?: () => void;
  /** Submitted snapshot only: reset to pending (same rules as tree `reset` on data package). */
  onResetToPending?: () => void | Promise<void>;
  /** When false, Edit Details is hidden (e.g. published snapshot). Must match parent's editability. */
  canEditDetails?: boolean;
  /** Number of samples in this data package — used to disable delete when submitted with samples. */
  nbSamples?: number;
}

const DataPackageFooter: FC<DataPackageFooterProps> = ({
  lastChangedDate,
  lastChangedBy,
  status,
  isReadOnly,
  hasChanges,
  onSave,
  onDelete,
  onCancel,
  setIsReadOnly,
  privilege,
  nodeId,
  canCreateSnapshot = false,
  isResumeSnapshot = false,
  onCreateSnapshot,
  isSnapshot = false,
  snapshotInProgress = false,
  snapshotProgress = 0,
  onSubmit,
  onPublish,
  onResetToPending,
  canEditDetails = true,
  nbSamples,
}) => {
  const { isAuthorized } = useAuthStore();
  const { showDeleteDialog } = useDialogStore();
  const { resolveUser } = useUserResolution();

  const formatStatus = (status: string | undefined) => {
    if (!status) return 'Not specified';

    // If this is a snapshot: show Published when published, otherwise Snapshot (with progress if in progress)
    if (isSnapshot) {
      if (status === StatusDto.Published) {
        const statusInfo = STATUS_MAP[status];
        return (
          <Chip
            label={statusInfo?.label ?? 'Published'}
            color={statusInfo?.chipColor ?? 'success'}
            size="small"
            icon={statusInfo?.icon}
            sx={{ ml: 1 }}
          />
        );
      }
      return (
        <Chip
          label={snapshotInProgress ? `Snapshot (${snapshotProgress}%)` : 'Snapshot'}
          color="info"
          size="small"
          icon={<CameraAlt />}
          sx={{ ml: 1 }}
        />
      );
    }

    const statusInfo = STATUS_MAP[status];
    const label = statusInfo?.label || status;
    const chipColor = statusInfo?.chipColor ?? 'default';

    return <Chip label={label} color={chipColor} size="small" icon={statusInfo?.icon} sx={{ ml: 1 }} />;
  };

  const handleDelete = async () => {
    try {
      const confirmed = await showDeleteDialog('Data Package', nodeId);
      if (confirmed) {
        onDelete();
      }
    } catch (error) {
      log.error('Error showing delete dialog:', error);
    }
  };

  const canEdit = isAuthorized(privilege, 'update');
  // Deletion is only allowed when the data package is empty (no samples) or still pending (not submitted/snapshot).
  const disableDelete = (nbSamples ?? 0) > 0 && status !== StatusDto.Pending;
  const canDelete = isAuthorized(privilege, 'delete');
  const canResetSubmittedSnapshot =
    !!isSnapshot &&
    !!onResetToPending &&
    status === StatusDto.Submitted &&
    isAuthorized(PrivilegeEnum.DataSnapshot, 'reset');

  // Snapshot visibility and state:
  // - Only for live (non-snapshot) and not yet published
  // - Hide completely if user lacks snapshot create privilege
  const isSnapshotCreateAuthorized = isAuthorized(PrivilegeEnum.DataSnapshot, 'create');
  const shouldShowSnapshotButton =
    status !== StatusDto.Published &&
    isSnapshotCreateAuthorized &&
    !!onCreateSnapshot &&
    (!isSnapshot || (isSnapshot && snapshotProgress < 100));

  const snapshotDisabled = !canCreateSnapshot || hasChanges || (snapshotInProgress && !isResumeSnapshot);
  const snapshotTooltip = (() => {
    if (!shouldShowSnapshotButton) return '';
    if (snapshotInProgress && !isResumeSnapshot) {
      return 'Snapshot creation is already in progress.';
    }
    if (hasChanges) {
      return 'Save or discard your changes before creating a snapshot.';
    }
    if (!canCreateSnapshot) {
      if (isSnapshot && snapshotInProgress) {
        return 'If progress stops for about 10 minutes, you can resume the stalled snapshot build.';
      }
      return 'Snapshot cannot be created yet. Complete all required fields and resolve any validation issues first.';
    }
    if (isResumeSnapshot) {
      return 'Queue the snapshot job again after the build appeared stuck (no updates for about 10 minutes).';
    }
    return 'Create a snapshot of this data package to freeze its contents for publication.';
  })();

  return (
    <Box>
      <Box
        sx={{
          borderTop: '1px solid #e0e0e0',
          mt: 3,
          pt: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          fontSize: '0.875rem',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body1" component="div" sx={{ fontSize: '1rem', flex: 1, mr: 2 }}>
          Last modification on{' '}
          <strong>{lastChangedDate ? format(new Date(lastChangedDate), 'PPpp') : 'Unknown'}</strong> by{' '}
          <strong>{lastChangedBy ? resolveUser(lastChangedBy) : 'Unknown'}</strong>.&nbsp; Current status:{' '}
          {formatStatus(status)}
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* 1. If Readonly and details are editable → show Edit button (never for e.g. published snapshots) */}
          {isReadOnly && canEdit && canEditDetails ? (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Edit />}
              size="large"
              onClick={() => setIsReadOnly(false)}
            >
              Edit Details
            </Button>
          ) : null}

          {/* 2. If Modified → show Save button */}
          {!isReadOnly && hasChanges ? (
            <Button variant="contained" color="success" size="large" startIcon={<SaveIcon />} onClick={onSave}>
              Save Changes
            </Button>
          ) : null}

          {/* 3. If Modified → show Cancel button */}
          {!isReadOnly ? (
            <Button variant="contained" color="error" size="large" startIcon={<ClearIcon />} onClick={onCancel}>
              Cancel
            </Button>
          ) : null}

          {/* 4. Pending Live: Create Snapshot button (always visible when allowed, disabled with tooltip if not ready) */}
          {shouldShowSnapshotButton ? (
            <Tooltip title={snapshotTooltip}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<CameraAlt />}
                  onClick={onCreateSnapshot}
                  disabled={snapshotDisabled}
                >
                  {isResumeSnapshot ? 'Resume stalled snapshot' : 'Create Snapshot'}
                </Button>
              </span>
            </Tooltip>
          ) : null}

          {/* 5. Pending Snapshot (complete): Submit button */}
          {!hasChanges &&
          status === StatusDto.Pending &&
          isSnapshot &&
          !snapshotInProgress &&
          snapshotProgress === 100 &&
          isAuthorized(PrivilegeEnum.DataSnapshot, 'submit') &&
          onSubmit ? (
            <Button variant="contained" color="primary" size="large" startIcon={<CheckCircleIcon />} onClick={onSubmit}>
              Submit
            </Button>
          ) : null}

          {/* 6a. Submitted Snapshot: Publish + Reset to pending */}
          {!hasChanges &&
          status === StatusDto.Submitted &&
          isSnapshot &&
          isAuthorized(PrivilegeEnum.DataSnapshot, 'execute') &&
          onPublish ? (
            <Button variant="contained" color="success" size="large" startIcon={<PublishIcon />} onClick={onPublish}>
              Publish
            </Button>
          ) : null}

          {!hasChanges && canResetSubmittedSnapshot ? (
            <Button
              variant="outlined"
              color="warning"
              size="large"
              startIcon={<RestartAltIcon />}
              onClick={() => void onResetToPending?.()}
            >
              Reset to pending
            </Button>
          ) : null}

          {/* 6b. Pending Live: Direct Publish button (alternative to snapshot path) */}
          {!hasChanges &&
          status === StatusDto.Pending &&
          !isSnapshot &&
          !canCreateSnapshot &&
          isAuthorized(PrivilegeEnum.DataPackage, 'execute') &&
          onPublish ? (
            <Button variant="contained" color="success" size="large" startIcon={<PublishIcon />} onClick={onPublish}>
              Publish Live
            </Button>
          ) : null}

          {/* 7. Show Delete button if not published */}
          {!hasChanges && status !== StatusDto.Published && canDelete ? (
            <Tooltip
              title={
                disableDelete
                  ? 'Cannot delete: data package has samples and has been submitted. Remove all samples first, or delete while still pending.'
                  : ''
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                  disabled={disableDelete}
                >
                  Delete Data Package
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      {/* Snapshot progress indicator */}
      {snapshotInProgress ? (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            Creating snapshot... {snapshotProgress}% complete
          </Typography>
          <LinearProgress variant="determinate" value={snapshotProgress} sx={{ height: 8, borderRadius: 4 }} />
        </Box>
      ) : null}
    </Box>
  );
};

export default DataPackageFooter;
